import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Pencil, Type } from 'lucide-react';

/**
 * Signature input that supports both Draw (canvas) and Type (cursive text) modes.
 * Props:
 *  - title: section header (e.g. "Industry Partner Signatory")
 *  - name, onNameChange: print-name input value + setter
 *  - designation, onDesignationChange (optional): designation row
 *  - testIdPrefix: data-testid scope ('signer' | 'witness')
 *  - onChange(dataUrl | ''): called whenever the signature data URL changes
 *
 * Renders a Draw/Type tab. In Type mode, the printed name is converted to a
 * cursive-font signature on an off-screen canvas → data URL.
 */
const SignatureInput = ({
  title,
  name,
  onNameChange,
  designation,
  onDesignationChange,
  showDesignation = false,
  testIdPrefix,
  onChange,
}) => {
  const [mode, setMode] = useState('draw');
  const [typedSig, setTypedSig] = useState('');
  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);

  // When typed value changes (or print name when typedSig empty), render to canvas
  useEffect(() => {
    if (mode !== 'type') return;
    const value = (typedSig || name || '').trim();
    if (!value) {
      onChange?.('');
      return;
    }
    const c = offscreenRef.current || document.createElement('canvas');
    offscreenRef.current = c;
    c.width = 600;
    c.height = 160;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = '#0f172a';
    ctx.font = 'italic 56px "Brush Script MT", "Lucida Handwriting", "Segoe Script", cursive';
    ctx.textBaseline = 'middle';
    ctx.fillText(value, 24, c.height / 2);
    onChange?.(c.toDataURL('image/png'));
  }, [mode, typedSig, name, onChange]);

  const handleDrawEnd = () => {
    if (canvasRef.current && !canvasRef.current.isEmpty()) {
      onChange?.(canvasRef.current.toDataURL('image/png'));
    } else {
      onChange?.('');
    }
  };

  const switchMode = (m) => {
    setMode(m);
    if (m === 'draw') {
      // Clear typed sig output until user actually draws something
      onChange?.('');
      setTimeout(() => canvasRef.current?.clear(), 0);
    } else {
      // Trigger re-render via useEffect dependency change
      setTypedSig((s) => s);
    }
  };

  const clear = () => {
    if (mode === 'draw') {
      canvasRef.current?.clear();
    } else {
      setTypedSig('');
    }
    onChange?.('');
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold text-ica-navy">{title}</h3>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
            <button
              type="button"
              data-testid={`${testIdPrefix}-mode-draw`}
              onClick={() => switchMode('draw')}
              className={`px-3 py-1 flex items-center gap-1 transition ${mode === 'draw' ? 'bg-ica-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <Pencil className="w-3 h-3" /> Draw
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-mode-type`}
              onClick={() => switchMode('type')}
              className={`px-3 py-1 flex items-center gap-1 border-l border-gray-200 transition ${mode === 'type' ? 'bg-ica-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <Type className="w-3 h-3" /> Type
            </button>
          </div>
        </div>
        <div>
          <Label>Print name *</Label>
          <Input
            data-testid={`${testIdPrefix}-name`}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
          />
        </div>
        {showDesignation && (
          <div>
            <Label>Designation</Label>
            <Input
              data-testid={`${testIdPrefix}-designation`}
              value={designation || ''}
              onChange={(e) => onDesignationChange?.(e.target.value)}
            />
          </div>
        )}
        <div>
          <Label>Signature *</Label>
          {mode === 'draw' ? (
            <div className="border border-gray-300 rounded bg-white">
              <SignatureCanvas
                ref={canvasRef}
                onEnd={handleDrawEnd}
                canvasProps={{
                  'data-testid': `${testIdPrefix}-canvas`,
                  className: 'w-full h-32',
                }}
                backgroundColor="rgb(255,255,255)"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                data-testid={`${testIdPrefix}-typed-input`}
                placeholder={`Type signature (defaults to your printed name)`}
                value={typedSig}
                onChange={(e) => setTypedSig(e.target.value)}
              />
              <div
                data-testid={`${testIdPrefix}-typed-preview`}
                className="border border-gray-200 rounded bg-white p-3 h-24 flex items-center"
                style={{
                  fontFamily: '"Brush Script MT", "Lucida Handwriting", "Segoe Script", cursive',
                  fontStyle: 'italic',
                  fontSize: '36px',
                  color: '#0f172a',
                  lineHeight: 1,
                }}
              >
                {(typedSig || name || '').trim() || (
                  <span className="text-gray-300 text-sm font-sans not-italic">Your typed signature will appear here…</span>
                )}
              </div>
              <p className="text-[11px] text-gray-500">
                By typing your name you confirm that this is your legal electronic signature.
              </p>
            </div>
          )}
        </div>
        <button
          type="button"
          data-testid={`${testIdPrefix}-clear`}
          onClick={clear}
          className="text-xs text-gray-500 hover:text-red-600"
        >
          Clear
        </button>
      </CardContent>
    </Card>
  );
};

export default SignatureInput;
